import { getConciergeGrants } from '@/hooks/use-concierge-permissions';
import { createHasPermission } from '@calm/evexia-web-common/dist/circle';
import { CircleCircle } from '@calm/evexia-web-sdk';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    useMutation,
    invalidateQueryKey,
    queryKeys,
} from '@rhg/libs/react-query';
import { circleRecommendationsQueryOptions } from '..';

export function useForceRefreshCircleRecommendations() {
    const { getAccessTokenSilently } = useAuth();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({ circle }: { circle?: CircleCircle }) => {
            const { canCreateConcierge: includeAiCarePlan } =
                getConciergeGrants(
                    createHasPermission(
                        circle?.cohortInfo?.permissionDetail?.perms
                    )
                );
            const data = await circleRecommendationsQueryOptions.queryFn({
                getAccessTokenSilently,
                circle,
                includeAiCarePlan,
                forceRefresh: true,
            });
            if (circle) {
                queryClient.setQueryData(
                    circleRecommendationsQueryOptions.queryKey(
                        circle.recipient.id,
                        includeAiCarePlan
                    ),
                    data ?? {}
                );
                void invalidateQueryKey(
                    circleRecommendationsQueryOptions.queryKey(
                        circle.recipient.id,
                        !includeAiCarePlan
                    )
                );
                // Also invalidate AI recommendations as they may have changed
                void invalidateQueryKey([
                    queryKeys.aiRecommendations,
                    circle.recipient.id,
                ]);
                // Invalidate For You recommendations so they reflect the latest user state
                void invalidateQueryKey([
                    queryKeys.forYouRecommendations,
                    circle.recipient.id,
                ]);
                // Invalidate poll caches so stale poll data from a previous sponsorship is cleared
                void invalidateQueryKey([queryKeys.poll]);
            }
            return data;
        },
    });

    return {
        forceRefreshRecommendations: mutate,
        forceRefreshRecommendationsAsync: mutateAsync,
        ...otherProps,
    };
}
